import { Component, OnInit, OnDestroy, Input, ViewChild, ElementRef, Output, EventEmitter } from '@angular/core';
import { TelemetryService } from 'src/app/service/telemetry.service';
import { TelemetryData } from 'src/app/model/telemetry-data';
import * as L from 'leaflet';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-telemetry-map',
  templateUrl: './telemetry-map.component.html',
  styleUrls: ['./telemetry-map.component.css']
})
export class TelemetryMapComponent implements OnInit, OnDestroy {
  @Input() carId!: string;
  @Input() showHistory: boolean = true;
  @Input() showControls: boolean = true;
  @Input() height: string = '600px';
  @Input() autoCenter: boolean = true;
  @Output() positionUpdated = new EventEmitter<TelemetryData>();
  
  @ViewChild('mapContainer', { static: false }) mapContainer!: ElementRef;
  
  private map!: L.Map;
  private marker!: L.Marker;
  private polyline!: L.Polyline;
  private path: L.LatLng[] = [];
  private subscription = new Subscription();
  private isInitialized = false;

  // Configurações do mapa
  defaultCenter: L.LatLngExpression = [-23.5505, -46.6333]; // São Paulo
  defaultZoom = 13;

  constructor(private telemetryService: TelemetryService) {}

  ngOnInit(): void {
    if (!this.carId) {
      //console.error('[TelemetryMap] carId is required');
      return;
    }
  }

  ngAfterViewInit(): void {
    // Pequeno delay para garantir que o container está renderizado
    setTimeout(() => {
      this.initMap();
      this.loadLastPosition();
      this.connectToTelemetry();
    }, 100);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    if (this.map) {
      this.map.remove();
    }
  }

  /**
   * Inicializa o mapa Leaflet
   */
  private initMap(): void {
    try {
      // Cria o mapa
      this.map = L.map('telemetry-map', {
        center: this.defaultCenter,
        zoom: this.defaultZoom,
        zoomControl: this.showControls
      });

      // Adiciona camada de tiles (OpenStreetMap)
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors',
        maxZoom: 19
      }).addTo(this.map);

      // Ícone customizado para o carro
      const carIcon = L.icon({
        iconUrl: 'assets/img/car-marker.png',
        iconSize: [40, 40],
        iconAnchor: [20, 20],
        popupAnchor: [0, -20]
      });

      // Cria marcador do veículo (inicialmente invisível)
      this.marker = L.marker([0, 0], { 
        icon: carIcon,
        opacity: 0 
      }).addTo(this.map);

      // Cria polyline para histórico de trajeto
      if (this.showHistory) {
        this.polyline = L.polyline([], { 
          color: '#667eea',
          weight: 4,
          opacity: 0.7,
          smoothFactor: 1
        }).addTo(this.map);
      }

      this.isInitialized = true;
      //console.log('[TelemetryMap] Map initialized');
    } catch (error) {
      //console.error('[TelemetryMap] Error initializing map:', error);
    }
  }

  /**
   * Carrega última posição conhecida
   */
  private loadLastPosition(): void {
    this.telemetryService.getLastKnownPosition(this.carId).subscribe({
      next: (data) => {
        //console.log('[TelemetryMap] Last position loaded');
        this.updateMarkerPosition(data);
      },
      error: (error) => {
        //console.log('[TelemetryMap] No last position available');
      }
    });
  }

  /**
   * Conecta ao stream de telemetria
   */
  private connectToTelemetry(): void {
    const streamSub = this.telemetryService.connectToRealtime(this.carId).subscribe({
      next: (data) => {
        this.updateMarkerPosition(data);
        this.positionUpdated.emit(data);
      },
      error: (error) => {
        //console.error('[TelemetryMap] Stream error:', error);
      }
    });

    this.subscription.add(streamSub);
  }

  /**
   * Atualiza posição do marcador no mapa
   */
  private updateMarkerPosition(data: TelemetryData): void {
    if (!this.isInitialized || !this.map) return;

    const latLng = L.latLng(data.latitude, data.longitude);
    
    // Atualiza posição do marcador
    this.marker.setLatLng(latLng);
    this.marker.setOpacity(1); // Torna visível

    // Rotaciona marcador se houver heading (direção)
    if (data.heading !== undefined) {
      const iconElement = this.marker.getElement();
      if (iconElement) {
        iconElement.style.transform = `rotate(${data.heading}deg)`;
        iconElement.style.transition = 'transform 0.3s ease';
      }
    }

    // Atualiza popup com informações
    const popupContent = this.buildPopupContent(data);
    this.marker.bindPopup(popupContent);

    // Adiciona ao histórico de trajeto
    if (this.showHistory && this.polyline) {
      this.path.push(latLng);
      this.polyline.setLatLngs(this.path);
      
      // Limita histórico a 500 pontos
      if (this.path.length > 500) {
        this.path.shift();
      }
    }

    // Centraliza mapa na primeira atualização ou se autoCenter estiver ativo
    if (this.path.length === 1 || this.autoCenter) {
      this.map.setView(latLng, this.map.getZoom());
    }
  }

  /**
   * Constrói conteúdo do popup
   */
  private buildPopupContent(data: TelemetryData): string {
    let content = `
      <div class="telemetry-popup">
        <h6><strong>Veículo ${data.carId}</strong></h6>
        <hr style="margin: 8px 0;">
    `;

    if (data.speed !== undefined) {
      content += `<p><strong>Velocidade:</strong> ${data.speed.toFixed(0)} km/h</p>`;
    }

    if (data.altitude !== undefined) {
      content += `<p><strong>Altitude:</strong> ${data.altitude.toFixed(0)} m</p>`;
    }

    if (data.heading !== undefined) {
      content += `<p><strong>Direção:</strong> ${data.heading.toFixed(0)}°</p>`;
    }

    content += `
        <p><strong>Coordenadas:</strong><br>
        ${data.latitude.toFixed(6)}°, ${data.longitude.toFixed(6)}°</p>
        <p style="font-size: 12px; color: #666; margin: 0;">
          ${new Date(data.timestamp).toLocaleString('pt-BR')}
        </p>
      </div>
    `;

    return content;
  }

  /**
   * Centraliza mapa na posição atual
   */
  centerOnVehicle(): void {
    if (this.marker && this.isInitialized) {
      const latLng = this.marker.getLatLng();
      if (latLng.lat !== 0 || latLng.lng !== 0) {
        this.map.setView(latLng, 15);
      }
    }
  }

  /**
   * Limpa histórico de trajeto
   */
  clearHistory(): void {
    if (this.polyline) {
      this.path = [];
      this.polyline.setLatLngs(this.path);
    }
  }

  /**
   * Ajusta zoom do mapa
   */
  setZoom(zoom: number): void {
    if (this.map) {
      this.map.setZoom(zoom);
    }
  }

  /**
   * Mostra/oculta histórico
   */
  toggleHistory(): void {
    if (this.polyline) {
      this.showHistory = !this.showHistory;
      if (this.showHistory) {
        this.polyline.addTo(this.map);
      } else {
        this.polyline.remove();
      }
    }
  }

  /**
   * Fita o mapa ao trajeto completo
   */
  fitBounds(): void {
    if (this.path.length > 0 && this.map) {
      const bounds = L.latLngBounds(this.path);
      this.map.fitBounds(bounds, { padding: [50, 50] });
    }
  }
}
